import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { get as apiGet, post, put, del } from '@/src/api/apiHandler';
import { showToast } from '@/src/components/toast/showToast';

// Interfaces
interface CartItemAPI {
  id: number;
  quantity: number;
  ProductId?: number;
  ProductIdentityId: number;
  properties: {
    property_id?: number;
    attribute_id?: number;
    name: string;
    value: string;
    label: string;
    property_price: boolean;
  }[];
  extra: {
    id: number;
    name: string;
    key?: string;
    price: number;
    raw_price?: number;
    percent?: number | null;
    price_mode?: string;
    scope?: string;
    calc_base?: string;
    totals?: {
      perItem?: number;
      perProduct?: number;
      perOrder?: number;
    };
    line_amount?: number;
    order_amount?: number;
    base_amount?: number;
    applied_quantity?: number;
  } | null;
  extras?: Array<{
    id: number;
    name: string;
    key?: string;
    price: number;
    raw_price?: number;
    percent?: number | null;
    price_mode?: string;
    scope?: string;
    calc_base?: string;
    totals?: {
      perItem?: number;
      perProduct?: number;
      perOrder?: number;
    };
    line_amount?: number;
    order_amount?: number;
    base_amount?: number;
    applied_quantity?: number;
  }>;
  product: { title: string; url: string; image: string; title_second?: string };
  priceDetails: {
    original: number;
    unit: number;
    hasDiscount: boolean;
    isVip: boolean;
    isCustomer: boolean; // Field for customer price
  };
  lineTotals?: {
    lineSubtotal?: number;
    extraPerItem?: number;
    extraPerProduct?: number;
    extraPerOrder?: number;
    finalLineTotal?: number;
  };
  finalPrice: number;
  shippingWeightKg: number;
  warehouse_count: number;
  warehouse_max_order_quantity?: number;
  warehouse_min_other_products?: number;
  minimumOtherProductsError?: string | null;
  isValid: boolean;
}

interface CartTotals {
  subtotal: number;
  extras: {
    perItem: number;
    perProduct: number;
    perOrder: number;
    total: number;
  };
  discount?: number;
  payable?: number;
  total: number;
}

interface OrderLevelExtra {
  id: number;
  name: string;
  amount: number;
  scope?: string;
  price_mode?: string;
  percent?: number | null;
  price?: number;
  base_amount?: number;
}

interface CartAPIResponse {
  items: CartItemAPI[];
  isValid: boolean;
  totals: CartTotals;
  orderLevelExtras?: OrderLevelExtra[];
  guest_id: string | null;
  coupon_code: string | null;
  messages: string[];
  snapshotHash: string;
}

interface CartState {
  cart: CartAPIResponse | null;
  guest_id: string | null;
  loading: boolean;
  error: string | null;
}

interface CartActions {
  fetchCart: () => Promise<void>;
  addItem: (item: {
    productIdentityId?: number;
    ProductId: number;
    quantity: number;
    ProductExtraIds?: number[];
  }) => Promise<void>;
  updateItem: (cartItemId: number, quantity: number) => Promise<void>;
  removeItem: (cartItemId: number) => Promise<void>;
  mergeCarts: (token: string) => Promise<void>;
  applyDiscount: (code: string) => Promise<boolean>;
  removeDiscount: () => Promise<void>;
  clearCart: () => void;
}

type CartServiceResponse = {
  data?: CartAPIResponse | null;
  message?: string;
};

let cartFetchPromise: Promise<void> | null = null;

const extractErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error && 'message' in error) {
    const maybeMessage = (error as { message?: unknown }).message;
    if (typeof maybeMessage === 'string') return maybeMessage;
  }
  return 'خطای ناشناخته رخ داده است';
};

const handleResponse = (response: CartServiceResponse, stateSetter: (partial: Partial<CartState>) => void) => {
  const cartData: CartAPIResponse | null = response?.data ?? null;
  stateSetter({
    cart: cartData,
    guest_id: cartData?.guest_id ?? null,
    loading: false,
    error: null,
  });

  if (cartData?.messages?.length) {
    cartData.messages.forEach((msg) => showToast(msg, 'info'));
  }

  if (response?.message && response.message !== 'success') {
    showToast(response.message, 'success');
  }
};

const useCartStore = create<CartState & CartActions>()(
  persist(
    (set, get) => ({
      cart: null,
      guest_id: null,
      loading: true,
      error: null,

      fetchCart: async () => {
        if (cartFetchPromise) return cartFetchPromise;

        const request = (async () => {
          set({ loading: true });
          try {
            const guestId = get().guest_id;
            const url = guestId ? `/card?guest_id=${guestId}` : '/card';
            const response = await apiGet(url);
            handleResponse(response, set);
          } catch (error) {
            set({ error: extractErrorMessage(error) });
          } finally {
            set({ loading: false });
          }
        })();

        cartFetchPromise = request;
        try {
          await request;
        } finally {
          if (cartFetchPromise === request) cartFetchPromise = null;
        }
      },

      addItem: async (item) => {
        set({ loading: true });
        try {
          const guestId = get().guest_id;
          const { productIdentityId, ProductExtraIds, ...rest } = item;
          const normalizedExtraIds = Array.isArray(ProductExtraIds)
            ? Array.from(
                new Set(
                  ProductExtraIds
                    .map((id) => Number(id))
                    .filter((id) => Number.isFinite(id)),
                ),
              )
            : [];
          const payload = {
            ...rest,
            guest_id: guestId,
            ...(typeof productIdentityId === 'number' ? { productIdentityId } : {}),
            ...(normalizedExtraIds.length > 0 ? { ProductExtraIds: normalizedExtraIds } : {}),
          };
          const response = await post('/card', payload);
          handleResponse(response, set);
        } catch (error) {
          const message = extractErrorMessage(error);
          set({ error: message });
          showToast(message || 'خطا در افزودن به سبد', 'error');
        } finally {
          set({ loading: false });
        }
      },

      updateItem: async (cartItemId, quantity) => {
        set({ loading: true });
        try {
          const guestId = get().guest_id;
          const payload = { quantity, guest_id: guestId };
          const response = await put(`/card-items/${cartItemId}`, payload);
          handleResponse(response, set);
        } catch (error) {
          set({ error: extractErrorMessage(error) });
        } finally {
          set({ loading: false });
        }
      },

      removeItem: async (cartItemId) => {
        set({ loading: true });
        try {
          const guestId = get().guest_id;
          const payload = { guest_id: guestId };
          const response = await del(`/card-items/${cartItemId}`, payload);
          handleResponse(response, set);
        } catch (error) {
          set({ error: extractErrorMessage(error) });
        } finally {
          set({ loading: false });
        }
      },

      mergeCarts: async (token) => {
        if (cartFetchPromise) await cartFetchPromise;
        const guestId = get().guest_id;
        if (!guestId || !token) return;

        try {
          const response = await post('/card-merge', { guest_id: guestId });
          handleResponse(response, set);
          set({ guest_id: null });
        } catch (error) {
          console.error("Failed to merge carts:", error);
          get().fetchCart();
        }
      },

      applyDiscount: async (code) => {
        set({ loading: true });
        try {
          const guestId = get().guest_id;
          const response = await post('/card/discount', { code, guest_id: guestId });
          handleResponse(response, set);
          return true;
        } catch (error) {
          const message = extractErrorMessage(error);
          set({ error: null });
          showToast(message || 'کد تخفیف معتبر نیست', 'error');
          return false;
        } finally {
          set({ loading: false });
        }
      },

      removeDiscount: async () => {
        set({ loading: true });
        try {
          const guestId = get().guest_id;
          const response = await del('/card/discount', { guest_id: guestId });
          handleResponse(response, set);
        } catch (error) {
          showToast(extractErrorMessage(error), 'error');
        } finally {
          set({ loading: false });
        }
      },

      clearCart: () => {
        set({ cart: null, guest_id: null, loading: false, error: null });
      },
    }),
    {
      name: 'guest-session-storage-v2',
      partialize: (state) => ({ guest_id: state.guest_id }),
    }
  )
);

export default useCartStore;
